import { h } from 'preact'
import { useIntlContext } from './IntlProvider'
import { useCustomContext } from '../context'
import { ALLOWED_CHARS } from './helpers'
import { Locale } from '../../v1/domain'

interface ValueMap {
  [s: string]: string | number | any[]
}

const smeGenitivMapping = {
  Olgešbellodat: 'Olgešbellodaga',
  Ovddádusbellodat: 'Ovddádusbellodaga',
  'Risttalaš Álbmotbellodat': 'Risttalaš Álbmotbellodaga',
  Gurutbellodat: 'Gurutbellodaga',

  Bargiidbellodat: 'Bargiidbellodaga',
  Guovddášbellodat: 'Guovddášbellodaga',
  'Sosialistalaš Gurutbellodat': 'Sosialistalaš Gurutbellodaga',
  Ruoksat: 'Ruoksada',
  Ruonát: 'Ruonáide',
  Árja: 'Árjja',
  'Sámeálbmot Bellodat': 'Sámeálbmot Bellodaga',
  'Kárášjot Lista': 'Kárášjot Listta',
  'Johttiidsámiid Listu': 'Johttiidsámiid Listtu',
  'Johttisápmelaččaid listu': 'Johttisápmelaččaid Listtu',
  'Sámeálbmot listu': 'Sámeálbmot listtu',
  'Deanu Sámelistu': 'Deanu Sámelisttu',
  'Sámeálbmot bellodat': 'Sámeálbmot bellodaga'
}

function genitiv(value, locale) {
  if (locale === 'nb' || locale === 'nn') {
    return value.endsWith('s') ? `${value}’` : `${value}s`
  }
  if (locale === 'sme') {
    return smeGenitivMapping[value] || value
  } else {
    return value
  }
}

const filters = {
  string: {
    year: (value, locale) => {
      const date = new Date(value)
      return new Intl.DateTimeFormat(locale, { year: 'numeric' }).format(date)
    },

    genitiv: (value, locale) => {
      return genitiv(value, locale)
    },

    default: value => {
      return value || ''
    }
  },
  number: {
    'ingen-desimaler': (value, locale) => {
      return new Intl.NumberFormat('nb', {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0
      }).format(value)
    },
    'en-desimal': (value, locale) => {
      return new Intl.NumberFormat('nb', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }).format(value)
    },
    'to-desimaler': (value, locale) => {
      return new Intl.NumberFormat('nb', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      }).format(value)
    },
    'en-desimal-uten-fortegn': (value, locale) => {
      return new Intl.NumberFormat('nb', {
        minimumFractionDigits: 1,
        maximumFractionDigits: 1
      }).format(value < 0 ? -1 * value : value)
    },
    default: (value, locale) => {
      return new Intl.NumberFormat('nb').format(value)
    }
  },
  array: {
    default: (value: string[], locale) => {
      const conjunction = locale === 'sme' ? 'ja' : 'og'

      const rest = [...value].map(item => filters[typeof item].default(item, locale))
      const last = rest.pop()

      return rest.length ? `${rest.join(', ')} ${conjunction} ${last}` : last
    }
  },
  object: {
    genitiv: (value, locale) => {
      const localizedValue = value[locale] || value.nb
      return genitiv(localizedValue, locale)
    },

    default: (value, locale) => {
      if (value.nb) {
        return value[locale] || value.nb
      }
      return JSON.stringify(value)
    }
  },
  undefined: {
    default: () => {
      return '– undefined –'
    }
  }
}

export function format(str: string = '', locale: string, map: ValueMap = {}) {
  return str.replace(/{([^{}]*)}/g, (_, expression) => {
    const [key, filter = 'default'] = expression.split(',').map(val => val.trim())
    const value = map[key]

    const type = Array.isArray(value) ? 'array' : typeof value
    const filterConfig = filters[type]
    if (!filterConfig) {
      throw Error(`Fant ikke filterkonfigurasjon for key '${key}' av type '${type}'`)
    }

    const filterFunction = filterConfig[filter] || filterConfig['default']
    if (!filterFunction) {
      throw new Error(`Fant ikke filter av type '${filter}' for '${type}'`)
    }

    const filteredValue = filterFunction(value, locale)
    if (ALLOWED_CHARS.test(filteredValue)) {
      return filteredValue
    } else {
      throw new Error(`Interpolering av verdi feilet: ${key} – ${JSON.stringify(filteredValue)}`)
    }
  })
}

export function generateKeys(id, modifiers) {
  const keys = Object.keys(modifiers)
    .filter(key => !!modifiers[key])
    .sort()

  const numberOfCombinations = Math.pow(2, keys.length)

  return Array(numberOfCombinations)
    .fill(0)
    .map((_, idx) => {
      const base2 = idx.toString(2).padStart(keys.length, '0')
      return keys.filter((_, idx) => base2[idx] === '1')
    })
    .sort((a, b) => a.length - b.length)
    .map(keys => keys.join('--'))
    .reverse()
    .map(key => [id, key].filter(Boolean).join('--'))
}

export type MessageProps = {
  id: string // 'key' is reserved
  as?: string
  values?: any
  modifiers?: any
  [id: string]: any
}

export function Message({ id, values = {}, as = 'span', modifiers, ...rest }: MessageProps) {
  // @ts-ignore
  return h(as, { dangerouslySetInnerHTML: { __html: message(id, values, modifiers) }, ...rest })
}

export function message(id: string, values?: any, messageModifiers?: any) {
  const { messages = {}, locale, modifiers } = useIntlContext()
  const { sendError } = useCustomContext()

  const key = generateKeys(id, { ...modifiers, ...messageModifiers }).find(key => !!messages[key])
  const msg = key ? messages[key] : ''

  try {
    return format(msg, locale, values)
  } catch (e) {
    sendError(`Formattering av melding med id ${id} feilet: ${e.message}`)
    return ''
  }
}

export function messageNoContext(messages = {}, locale, sendError, id: string, values?: any) {
  const key = generateKeys(id, {}).find(key => !!messages[key])
  const msg = key ? messages[key] : ''
  try {
    return format(msg, locale, values)
  } catch (e) {
    sendError(`Formattering av melding med id ${id} feilet: ${e.message}`)
    return ''
  }
}

type LocaleDescription = 'Bokmål' | 'Nynorsk' | 'Nordsamisk'

export function desc(locale: Locale): LocaleDescription {
  switch (locale) {
    case 'nb':
      return 'Bokmål'
    case 'nn':
      return 'Nynorsk'
    case 'sme':
      return 'Nordsamisk'
    default:
      throw new Error('Ukjent locale ' + locale)
  }
}
